import {
    ButtonGradientPrimary,
    Colors,
    Heading,
    Icon,
    Text,
    type IconName,
} from '@amelio/ui-library';
import { useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';

import { anonymousRespondentCopy, type RespondentStanding } from './anonymousRespondentCopy';
import { PrivacySentence } from './PrivacySentence';

import type { User } from '@/types/directory/User';
import type { PropsWithChildren } from 'react';

import {
    FeedbackPrivacy,
    isAnonymousFeedback,
    revieweeLaunchedCopy,
    type FeedbackPrivacyCopyKey,
    type FeedbackPrivacyValue,
} from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import {
    SurveyWindow,
    SurveyWindowContent,
} from '@/pages/SurveyAnswer/SurveyAnswer/components/Container/SurveyWindow';
import { SurveyAnswerFrame } from '@/pages/SurveyAnswer/SurveyAnswer/components/SurveyAnswerFrame';
import { isolateDirection } from '@/utils/bidi';
import { getFullName } from '@/utils/user';

interface PrivacyLine {
    icon: IconName;
    texts: FeedbackPrivacyCopyKey[];
}

interface PrivacyCopy {
    title: FeedbackPrivacyCopyKey;
    titleWithName: FeedbackPrivacyCopyKey;
    lead?: FeedbackPrivacyCopyKey;
}

const COPY: Record<FeedbackPrivacyValue, PrivacyCopy> = {
    [FeedbackPrivacy.ANONYMOUS]: {
        title: 'answer.feedbackPrivacy.anonymous.title',
        titleWithName: 'answer.feedbackPrivacy.anonymous.titleWithName',
    },
    [FeedbackPrivacy.NAMED]: {
        title: 'answer.feedbackPrivacy.named.title',
        titleWithName: 'answer.feedbackPrivacy.named.titleWithName',
        lead: 'answer.feedbackPrivacy.named.lead',
    },
};

const ICON_HIDDEN: IconName = 'security-eye-slash';
const ICON_SHOWN: IconName = 'security-eye';
const ICON_PROMISED: IconName = 'security-shield-tick';

/** What the manager sees, what stays hidden, and what the person evaluated sees, as this respondent stands. */
const linesOf = (
    feedbackPrivacy: FeedbackPrivacyValue,
    standing: RespondentStanding,
): PrivacyLine[] => {
    const revieweeIcon = standing.launchedByReviewee ? ICON_SHOWN : ICON_HIDDEN;

    if (!isAnonymousFeedback(feedbackPrivacy)) {
        return [
            {
                icon: ICON_SHOWN,
                texts: ['answer.feedbackPrivacy.named.seen'],
            },
            {
                icon: revieweeIcon,
                texts: [
                    standing.launchedByReviewee
                        ? revieweeLaunchedCopy(feedbackPrivacy)
                        : 'answer.feedbackPrivacy.named.reviewee',
                ],
            },
        ];
    }

    const { hidden, revieweeSentence, conditions } = anonymousRespondentCopy(standing);

    return [
        { icon: ICON_HIDDEN, texts: [hidden] },
        { icon: revieweeIcon, texts: [revieweeSentence] },
        { icon: ICON_PROMISED, texts: conditions },
    ];
};

/** The word of the title that says the promise, framed with its padlock. */
const FramedPromise = ({ children }: PropsWithChildren) => (
    <span
        className={
            'inline-flex items-center gap-1 rounded-lg border-2 border-white px-1 whitespace-nowrap'
        }
    >
        <Icon
            name={'security-lock'}
            color={Colors.WHITE}
            size={'lg'}
            ariaHidden
            className={'shrink-0'}
        />
        {children}
    </span>
);

export interface FeedbackPrivacyNoticeProps {
    feedbackPrivacy: FeedbackPrivacyValue;
    /** Completed responses an anonymous feedback needs before its answers become visible. */
    minimumResponses: number;
    reviewee?: User;
    /** Who the respondent is to the results, so the notice promises only what holds for them. */
    respondentStanding?: RespondentStanding;
    isSelfEvaluation?: boolean;
    backgroundImageUrl?: string;
    onAcknowledge: () => void;
}

/**
 * The first screen of a 360's survey window, before its first question: whether the manager will see the respondent's
 * name, what stays hidden, and what the person evaluated will see. Each mode only promises what the
 * server enforces for it.
 */
export const FeedbackPrivacyNotice = ({
    feedbackPrivacy,
    minimumResponses,
    reviewee,
    respondentStanding = {},
    isSelfEvaluation,
    backgroundImageUrl,
    onAcknowledge,
}: FeedbackPrivacyNoticeProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();

    const windowRef = useRef<HTMLDivElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);

    // The tips before it leave the page scrolled down, past this window, and the keyboard on a button now gone.
    useEffect(() => {
        windowRef.current?.scrollIntoView?.({ block: 'start' });
        titleRef.current?.focus({ preventScroll: true });
    }, []);

    const copy = COPY[feedbackPrivacy];
    const values = {
        managerLabel: getManagerLabel(),
        name: reviewee ? isolateDirection(getFullName(reviewee)) : '',
        minimum: minimumResponses,
    };

    return (
        <SurveyAnswerFrame
            reviewee={reviewee}
            isSelfEvaluation={isSelfEvaluation}
            feedbackPrivacy={feedbackPrivacy}
            respondentStanding={respondentStanding}
        >
            <div
                ref={windowRef}
                className={'survey-gap scroll-mt-24'}
            >
                <SurveyWindow
                    backgroundImageUrl={backgroundImageUrl}
                    cardBottom={
                        <div
                            className={
                                'absolute inset-x-6 bottom-6 flex justify-center sm:inset-x-12'
                            }
                        >
                            <ButtonGradientPrimary
                                size={'lg'}
                                fitContent
                                className={'w-full sm:w-auto'}
                                onClick={onAcknowledge}
                                rightIcon={
                                    <Icon
                                        name={'arrow-arrow-right'}
                                        ariaHidden
                                    />
                                }
                                dataTestId={'feedback-privacy-acknowledge'}
                            >
                                {t('answer.feedbackPrivacy.acknowledge')}
                            </ButtonGradientPrimary>
                        </div>
                    }
                >
                    <SurveyWindowContent>
                        <div
                            className={'flex w-full max-w-2xl flex-col gap-5 pb-6'}
                            data-testid={'feedback-privacy-notice'}
                        >
                            <div
                                ref={titleRef}
                                tabIndex={-1}
                                className={'flex flex-col gap-3 outline-none'}
                                data-testid={'feedback-privacy-title'}
                            >
                                <Heading
                                    level={2}
                                    align={'center'}
                                    color={Colors.WHITE}
                                    className={'text-xl font-semibold sm:text-2xl'}
                                >
                                    <Trans
                                        i18nKey={reviewee ? copy.titleWithName : copy.title}
                                        components={{ lock: <FramedPromise /> }}
                                        values={values}
                                    />
                                </Heading>

                                {copy.lead && (
                                    <Text
                                        color={Colors.WHITE}
                                        align={'center'}
                                    >
                                        {t(copy.lead)}
                                    </Text>
                                )}
                            </div>

                            <ul className={'divide-y divide-white/30 border-t border-white/30'}>
                                {linesOf(feedbackPrivacy, respondentStanding).map(
                                    ({ icon, texts }) => (
                                        <li
                                            key={texts[0]}
                                            className={'flex items-start gap-3 py-3'}
                                        >
                                            <Icon
                                                name={icon}
                                                color={Colors.WHITE}
                                                size={'lg'}
                                                ariaHidden
                                                className={'shrink-0'}
                                            />

                                            <div className={'min-w-0'}>
                                                <Text color={Colors.WHITE}>
                                                    {texts.map((text, index) => (
                                                        <span key={text}>
                                                            {index > 0 && ' '}
                                                            <PrivacySentence
                                                                copyKey={text}
                                                                values={values}
                                                            />
                                                        </span>
                                                    ))}
                                                </Text>
                                            </div>
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    </SurveyWindowContent>
                </SurveyWindow>
            </div>
        </SurveyAnswerFrame>
    );
};
